import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Repeat, Pencil, Trash2, Play, Pause } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import {
  useRecurringExpenses,
  useCreateRecurringExpense,
  useUpdateRecurringExpense,
  useDeleteRecurringExpense,
  useActivateRecurringExpense,
  useDeactivateRecurringExpense,
  useExpenseCategories,
} from '@/hooks/use-payroll';
import { useTechProfiles } from '@/hooks/use-crew';
import { usePlatformModules } from '@/hooks/use-modules';
import { useOrgCurrency, useOrgToday } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { RecurringExpense, CreateRecurringExpensePayload, ExpensePolicyFrequency } from '@/lib/types';

const frequencies: { value: ExpensePolicyFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

const getEmptyForm = (orgToday: string): CreateRecurringExpensePayload => ({
  tech: null,
  category: 0,
  description: '',
  amount: '',
  vendor: '',
  frequency: 'monthly',
  start_date: orgToday,
  end_date: null,
  is_active: true,
  next_generation: orgToday,
});

export function RecurringExpensesPage() {
  const orgCurrency = useOrgCurrency();
  const orgToday = useOrgToday();
  const { isModuleEnabled } = usePlatformModules();
  const { data: recurring, isLoading } = useRecurringExpenses();
  const { data: categories } = useExpenseCategories({ is_active: true });
  const { data: techs } = useTechProfiles({ page_size: 100 }, isModuleEnabled('crew'));
  const create = useCreateRecurringExpense();
  const update = useUpdateRecurringExpense();
  const remove = useDeleteRecurringExpense();
  const activate = useActivateRecurringExpense();
  const deactivate = useDeactivateRecurringExpense();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CreateRecurringExpensePayload>(() => getEmptyForm(orgToday));

  const handleEdit = (item: RecurringExpense) => {
    setEditingId(item.id);
    setForm({
      tech: item.tech,
      category: item.category,
      description: item.description,
      amount: item.amount,
      vendor: item.vendor,
      frequency: item.frequency,
      start_date: item.start_date,
      end_date: item.end_date,
      is_active: item.is_active,
      next_generation: item.next_generation,
    });
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm(getEmptyForm(orgToday));
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await update.mutateAsync({ id: editingId, ...form });
    } else {
      await create.mutateAsync(form);
    }
    setShowForm(false);
    setEditingId(null);
    setForm(getEmptyForm(orgToday));
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Delete this recurring expense?')) {
      remove.mutate(id);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/payroll/expenses">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Recurring Expenses</h1>
            <p className="text-gray-600 mt-1">Manage automatically generated expenses</p>
          </div>
        </div>
        <Button onClick={handleNew}>
          <Plus className="h-4 w-4 mr-2" />
          New Recurring Expense
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Recurring Expense' : 'New Recurring Expense'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Description *</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="e.g., Monthly vehicle insurance"
                    required
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select
                    value={form.category ? String(form.category) : ''}
                    onValueChange={(v) => setForm((p) => ({ ...p, category: Number(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories?.results?.map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Amount *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Label>Vendor</Label>
                  <Input
                    value={form.vendor ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, vendor: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label>Technician (optional)</Label>
                  <Select
                    value={form.tech ? String(form.tech) : 'none'}
                    onValueChange={(v) =>
                      setForm((p) => ({ ...p, tech: v === 'none' ? null : Number(v) }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Organization-level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Organization-level</SelectItem>
                      {techs?.results?.map((tech) => (
                        <SelectItem key={tech.id} value={String(tech.id)}>
                          {tech.first_name} {tech.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Frequency *</Label>
                  <Select
                    value={form.frequency}
                    onValueChange={(v) =>
                      setForm((p) => ({ ...p, frequency: v as ExpensePolicyFrequency }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {frequencies.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label>End Date (optional)</Label>
                  <Input
                    type="date"
                    value={form.end_date ?? ''}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, end_date: e.target.value || null }))
                    }
                  />
                </div>
                <div>
                  <Label>Next Generation Date *</Label>
                  <Input
                    type="date"
                    value={form.next_generation}
                    onChange={(e) => setForm((p) => ({ ...p, next_generation: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={create.isPending || update.isPending}>
                  {editingId ? 'Update' : 'Create'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : !recurring?.results?.length ? (
            <div className="p-8 text-center text-gray-500">
              <Repeat className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No recurring expenses</p>
              <p className="text-sm mt-1">Set up recurring expenses to auto-generate them on a schedule</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {recurring.results.map((item) => (
                <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">{item.description}</h3>
                        <Badge variant={item.is_active ? 'default' : 'secondary'}>
                          {item.is_active ? 'Active' : 'Paused'}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                        <span>{formatMoney(item.amount, orgCurrency)}</span>
                        <span>{item.frequency_display}</span>
                        <span>{item.category_name}</span>
                        {item.tech_name && <span>{item.tech_name}</span>}
                        {item.vendor && <span>{item.vendor}</span>}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Next: {item.next_generation}
                        {item.last_generated && ` | Last: ${item.last_generated}`}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {item.is_active ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deactivate.mutate(item.id)}
                          title="Pause"
                        >
                          <Pause className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => activate.mutate(item.id)}
                          title="Activate"
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(item)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
